import { Mail, Phone, MapPin, Clock, MessageCircle, Navigation2, Star } from "lucide-react";
import * as Lucide from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CLINIC, HOURS, MAPS_URLS } from "../../lib/clinic";
import { trackEvent } from "../../lib/analytics";
import SocialIcons from "./SocialIcons";
import CoFinanciamentoBar from "./CoFinanciamentoBar";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer id="contactos" data-testid="site-footer" className="bg-brand-600 text-slate-200">
      <div className="container-icb py-16">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src={CLINIC.logo} alt="ICB" className="h-14 w-auto object-contain brightness-0 invert" />
            </div>
            <p className="text-sm text-slate-300/90 leading-relaxed">{t("footer.intro")}</p>
            <div className="mt-5" data-testid="footer-socials">
              <p className="text-[10px] uppercase tracking-[0.22em] text-gold-300/90 mb-2.5">Siga-nos</p>
              <SocialIcons size={36} location="footer" tone="dark" />
            </div>
          </div>

          <div>
            <h4 className="font-serif text-white text-lg mb-5">{t("footer.contacts")}</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin size={16} className="text-gold-300 mt-0.5 flex-shrink-0" />
                <a
                  href={MAPS_URLS.directions}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("click_directions", { source: "footer_address", location: "contacts_address" })}
                  data-testid="footer-address-link"
                  aria-label={`Ver direções para ${CLINIC.address} no Google Maps`}
                  className="group inline-flex items-start gap-1.5 hover:text-gold-300 transition-colors"
                >
                  <span>{CLINIC.address}</span>
                  <Navigation2
                    size={13}
                    className="mt-0.5 shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 text-gold-300"
                  />
                </a>
              </li>
              <li className="flex gap-3"><Phone size={16} className="text-gold-300 mt-0.5 flex-shrink-0" /><a href={`tel:${CLINIC.phoneRaw}`} onClick={() => trackEvent("click_phone", { source: "footer" })} data-testid="footer-phone" className="hover:text-gold-300">{CLINIC.phone}</a></li>
              <li className="flex gap-3"><MessageCircle size={16} className="text-gold-300 mt-0.5 flex-shrink-0" /><a href={`https://wa.me/${CLINIC.whatsappRaw}`} target="_blank" rel="noreferrer" onClick={() => trackEvent("click_whatsapp", { source: "footer" })} data-testid="footer-whatsapp" className="hover:text-gold-300">{CLINIC.whatsapp}</a></li>
              <li className="flex gap-3"><Mail size={16} className="text-gold-300 mt-0.5 flex-shrink-0" /><a href={`mailto:${CLINIC.email}`} onClick={() => trackEvent("click_email", { source: "footer" })} data-testid="footer-email" className="hover:text-gold-300">{CLINIC.email}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-white text-lg mb-5"><Clock size={16} className="inline mr-2" /> {t("footer.schedule")}</h4>
            <ul className="space-y-3 text-sm">
              {HOURS.map((h) => (
                <li key={h.dayKey} className="py-1 border-b border-white/10 last:border-0">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{t(h.dayKey)}</p>
                  <p className="text-slate-100">{h.hKey ? t(h.hKey) : h.h}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="aspect-[21/9] md:aspect-[3/1] rounded-xl overflow-hidden shadow-2xl">
          <iframe title="Localização ICB" src={MAPS_URLS.embed} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="map-embed" />
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center" data-testid="map-actions">
          <a
            href={MAPS_URLS.directions}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("click_directions", { source: "footer_map" })}
            data-testid="btn-directions"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 border border-white/15 text-white hover:bg-white/20 transition text-sm font-semibold"
          >
            <Navigation2 size={16} /> Como chegar
          </a>
          <a
            href={MAPS_URLS.writeReview}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("click_reviews", { source: "footer_map" })}
            data-testid="btn-reviews"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gold-400 hover:bg-gold-500 text-brand-700 transition text-sm font-semibold shadow"
          >
            <Star size={16} /> Avalie-nos no Google
          </a>
        </div>

        <div className="mt-8 text-center" data-testid="footer-areas">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-300 mb-2">{t("footer.areas.title")}</p>
          <p className="text-sm text-slate-300/85 max-w-3xl mx-auto leading-relaxed">{t("footer.areas.text")}</p>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          {/* 1) Licenciamento clínico — texto simples, antes da barra de badges */}
          <p className="text-[13px] text-slate-400 leading-relaxed text-center py-2 max-w-5xl mx-auto" data-testid="footer-clinical-licensing">
            Entidade: Dental Council, Lda · NIF: 508899885 · N.º Registo ERS: E118283 · Licença ARS Algarve: 4812/2012 · Direcção Clínica: Dr. Tiago Pires Frazão
          </p>

          {/* 2) Barra de badges (IEFP · Algarve 2030 · Portugal 2030 · UE) */}
          <CoFinanciamentoBar />

          {/* 3) Copyright + links — no final, centrado */}
          <div className="pt-4 text-[13px] text-slate-300 leading-relaxed text-center" data-testid="footer-copyright">
            <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
            <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1 items-center justify-center">
              <Link to="/politica-privacidade" data-testid="footer-privacy" className="hover:text-gold-300 transition-colors">{t("footer.privacy")}</Link>
              <span className="text-white/30">·</span>
              <Link
                to="/financiamento"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-financing-cta"
                onClick={() => trackEvent("click_financing", { source: "footer_links" })}
                className="hover:text-gold-300 transition-colors"
              >
                Financiamento
              </Link>
              <span className="text-white/30">·</span>
              <a
                href="https://www.livroreclamacoes.pt/inicio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-livro-reclamacoes"
                className="inline-flex items-center gap-1.5 hover:text-gold-300 transition-colors"
              >
                <Lucide.BookOpen size={12} /> {t("footer.livro")}
              </a>
              <span className="text-white/30">·</span>
              <span className="text-slate-400">{t("footer.tagline")}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
